import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import config from "../../config";
import { announcementLanguagesFromSettings } from "./stopAnnouncement";

export default function useStopArrivalAnnouncement(announcement, settings, adPlaying = false) {
  const announcementLanguages = useMemo(
    () => announcementLanguagesFromSettings(settings),
    [settings],
  );
  const languageSwitchIntervalMs = Math.max(
    0,
    Number(settings?.languageSwitchInterval || 0),
  ) * 1000;
  const [soundBlocked, setSoundBlocked] = useState(false);
  const queueRef = useRef(Promise.resolve());
  const activeAudioRef = useRef(null);
  const activeAudioUrlRef = useRef(null);
  const pendingPlayRetryRef = useRef(null);
  const mountedRef = useRef(true);
  const lastKeyRef = useRef("");
  const adPlayingRef = useRef(false);
  const deferredRef = useRef(false);
  const finishActivePlaybackRef = useRef(null);
  adPlayingRef.current = Boolean(adPlaying);

  const cleanupActiveAudio = useCallback(() => {
    pendingPlayRetryRef.current = null;
    const finishPlayback = finishActivePlaybackRef.current;
    finishActivePlaybackRef.current = null;
    const activeAudio = activeAudioRef.current;
    if (activeAudio) {
      activeAudio.onended = null;
      activeAudio.onerror = null;
      activeAudio.pause();
      activeAudio.removeAttribute("src");
      activeAudio.load();
      activeAudioRef.current = null;
    }
    if (activeAudioUrlRef.current) {
      URL.revokeObjectURL(activeAudioUrlRef.current);
      activeAudioUrlRef.current = null;
    }
    if (finishPlayback) finishPlayback();
  }, []);

  const enableAnnouncementSound = useCallback(() => {
    setSoundBlocked(false);
    const retry = pendingPlayRetryRef.current;
    if (retry) {
      pendingPlayRetryRef.current = null;
      retry();
    }
  }, []);

  useEffect(() => {
    const onInteract = () => enableAnnouncementSound();
    window.addEventListener("pointerdown", onInteract);
    window.addEventListener("keydown", onInteract);
    return () => {
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
    };
  }, [enableAnnouncementSound]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanupActiveAudio();
    };
  }, [cleanupActiveAudio]);

  useEffect(() => {
    if (adPlaying) {
      if (activeAudioRef.current) deferredRef.current = true;
      cleanupActiveAudio();
      if (announcement?.dedupeKey) {
        const playKey = `${announcementLanguages.join(",")}:${announcement.dedupeKey}`;
        if (lastKeyRef.current !== playKey) deferredRef.current = true;
      }
      return;
    }

    if (!announcement?.dedupeKey || announcementLanguages.length === 0) {
      if (!announcement) lastKeyRef.current = "";
      return;
    }
    const playKey = `${announcementLanguages.join(",")}:${announcement.dedupeKey}`;
    if (!deferredRef.current && lastKeyRef.current === playKey) return;
    deferredRef.current = false;
    lastKeyRef.current = playKey;

    const lines = [announcement, announcement.schedule].filter((line) => line?.phase);
    queueRef.current = queueRef.current.catch(() => null).then(async () => {
      for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
        const line = lines[lineIndex];
        for (let index = 0; index < announcementLanguages.length; index += 1) {
        const language = announcementLanguages[index];
        if (!mountedRef.current || adPlayingRef.current) {
          deferredRef.current = true;
          return;
        }

        try {
          const response = await fetch(`${config.baseURL}/announcement/tts`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              language,
              phase: line.phase,
              vehicle_name: announcement.vehicleName,
              stop_name: announcement.destination,
              minutes: line.minutes,
              seconds: line.seconds,
            }),
          });

          if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(error?.error || "Failed to generate announcement audio");
          }

          const audioBlob = await response.blob();
          if (!mountedRef.current || adPlayingRef.current) {
            deferredRef.current = true;
            return;
          }
          cleanupActiveAudio();

          const playableBlob = audioBlob.type === "audio/mpeg"
            ? audioBlob
            : new Blob([audioBlob], { type: "audio/mpeg" });
          const audioUrl = URL.createObjectURL(playableBlob);
          activeAudioUrlRef.current = audioUrl;

          await new Promise((resolve) => {
            const audio = new Audio(audioUrl);
            audio.preload = "auto";
            audio.volume = 1;
            activeAudioRef.current = audio;
            let settled = false;

            const finish = () => {
              if (settled) return;
              settled = true;
              if (finishActivePlaybackRef.current === finish) {
                finishActivePlaybackRef.current = null;
              }
              pendingPlayRetryRef.current = null;
              if (activeAudioRef.current === audio) activeAudioRef.current = null;
              if (activeAudioUrlRef.current === audioUrl) {
                URL.revokeObjectURL(audioUrl);
                activeAudioUrlRef.current = null;
              }
              resolve();
            };

            const startPlayback = () => {
              audio.play().then(() => setSoundBlocked(false)).catch((error) => {
                console.error("Error playing stop announcement:", error);
                if (error?.name === "NotAllowedError") {
                  setSoundBlocked(true);
                  pendingPlayRetryRef.current = startPlayback;
                  return;
                }
                finish();
              });
            };

            finishActivePlaybackRef.current = finish;
            audio.onended = finish;
            audio.onerror = finish;
            if (adPlayingRef.current) {
              deferredRef.current = true;
              finish();
              return;
            }
            startPlayback();
          });
        } catch (error) {
          console.error("Error playing stop announcement:", error);
        }

        if (index < announcementLanguages.length - 1 && languageSwitchIntervalMs) {
          await new Promise((resolve) => setTimeout(resolve, languageSwitchIntervalMs));
        }
        }
      }
    });
  }, [
    announcement,
    adPlaying,
    announcementLanguages,
    cleanupActiveAudio,
    languageSwitchIntervalMs,
  ]);

  return { soundBlocked, enableAnnouncementSound };
}
